import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import BottomNavigation from '@/components/BottomNavigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, BadgeCheck, Users, Eye, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Profile {
  id: string;
  followers_count: number;
  total_views: number;
  role: 'customer' | 'vendor' | 'driver' | 'admin';
}

const GetVerifiedPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchProfile();
  }, [user?.id]);

  const fetchProfile = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, followers_count, total_views, role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!user?.id || !profile) return;

    try {
      setSubmitting(true);
      
      const { error } = await supabase
        .from('verification_requests')
        .insert({
          user_id: user.id,
          followers_count: profile.followers_count,
          views_count: profile.total_views,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: "Verification Request Submitted",
        description: "Your verification request has been submitted. You'll hear back from us within 5-7 business days.",
      });

      navigate('/settings');
    } catch (error) {
      console.error('Error submitting verification request:', error);
      toast({
        title: "Error",
        description: "Failed to submit verification request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const hasRequiredFollowers = (profile?.followers_count || 0) >= 1000;
  const hasRequiredViews = (profile?.total_views || 0) >= 5000;
  const canSubmit = hasRequiredFollowers && hasRequiredViews;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Get Verified</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Verification Badge */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <BadgeCheck className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-xl font-bold mb-2">Get Verified on GAMBGO</h2>
              <p className="text-gray-600">
                Join our verified creators and unlock exclusive features
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Verification Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              {hasRequiredFollowers ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div className="flex-1">
                <p className="font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  1,000+ Followers
                </p>
                <p className="text-sm text-gray-600">
                  Current: {profile?.followers_count || 0} followers
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {hasRequiredViews ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div className="flex-1">
                <p className="font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  5,000+ Views (Last 30 Days)
                </p>
                <p className="text-sm text-gray-600">
                  Current: {profile?.total_views || 0} total views
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card>
          <CardHeader>
            <CardTitle>Verification Benefits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
              <p className="text-sm">Verified checkmark next to your name</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
              <p className="text-sm">Access to gallery uploads (not just camera)</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
              <p className="text-sm">Priority customer support</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
              <p className="text-sm">Exclusive creator features and tools</p>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmitRequest}
          disabled={!canSubmit || submitting}
          className={`w-full py-3 ${
            canSubmit 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Submitting Request...' : 
           canSubmit ? 'Submit Verification Request' : 
           'Requirements Not Met'}
        </Button>

        {!canSubmit && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Complete the requirements above to submit your verification request.
            </p>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default GetVerifiedPage;